import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import logoImg from '../assets/images/logo-image.png';

/* --- External links --- */
const CONSTRUCTION_DASHBOARD_URL = 'https://universal-construction-dashboard.onrender.com';

/* --- Helpers --- */
const bp = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
};

function useLockBodyScroll(locked) {
  useEffect(() => {
    const { body } = document;
    const prev = body.style.overflow;
    if (locked) body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev;
    };
  }, [locked]);
}

/* --- Styles --- */
const NavbarContainer = styled.nav`
  position: fixed;
  inset: 0 0 auto 0;
  height: 80px !important; /* fixed navbar height */
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
  backdrop-filter: blur(10px);
  z-index: 1000;
  padding: 0 1rem;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;

  @media (min-width: ${bp.md}) {
    height: 72px;
    padding: 0 2rem;
  }
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  height: 100%;
`;

const Logo = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
`;

const LogoImage = styled.img`
  width: 150px !important;   /* ✅ fixed logo size */
  height: 60px !important;
  object-fit: contain;
  border-radius: 8px;

  @media (min-width: ${bp.md}) {
    width: 60px;  /* slightly bigger on desktop */
    height: 60px;
  }
`;

const Spacer = styled.div``;

const NavLinks = styled.div`
  display: none;
  align-items: center;
  gap: clamp(0.35rem, 1.1vw, 1.25rem);
  flex-wrap: nowrap;

  @media (min-width: ${bp.md}) {
    display: flex;
  }

  @media (min-width: ${bp.lg}) {
    gap: clamp(0.75rem, 1.4vw, 1.5rem);
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  padding: 0.5rem clamp(0.5rem, 1vw, 0.9rem);
  border-radius: 8px;
  white-space: nowrap;
  transition: transform 0.25s ease, background 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const CTAButton = styled(Link)`
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: white;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  padding: 0.6rem clamp(0.8rem, 1.4vw, 1.2rem);
  border-radius: 999px;
  text-decoration: none;
  font-weight: 700;
  white-space: nowrap;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 4px 15px rgba(255, 145, 77, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 145, 77, 0.45);
  }
`;

const DashboardButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: linear-gradient(45deg, #3dccc7, #2a8f89);
  color: white;
  font-size: clamp(0.8rem, 1vw, 0.95rem);
  padding: 0.6rem clamp(0.8rem, 1.4vw, 1.2rem);
  border-radius: 999px;
  text-decoration: none;
  font-weight: 700;
  white-space: nowrap;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 4px 15px rgba(61, 204, 199, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(61, 204, 199, 0.45);
  }
`;

const MobileDashboardButton = styled(DashboardButton)`
  justify-content: center;
  text-align: center;
`;

const MobileMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  color: white;
  font-size: 1.75rem;
  cursor: pointer;
  padding: 0.35rem;
  border-radius: 10px;

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.9);
    outline-offset: 3px;
  }

  @media (min-width: ${bp.md}) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: absolute;
  inset: 100% 0 auto 0;
  display: ${(props) => (props.$open ? 'block' : 'none')};
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  padding: 0.75rem 1rem 1rem;

  @media (min-width: ${bp.md}) {
    display: none;
  }
`;

const MobileNavLinks = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const MobileNavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.9rem 0.9rem;
  border-radius: 10px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

/* --- Component --- */
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);

  useLockBodyScroll(open);

  const toggle = () => setOpen((o) => !o);
  const close = () => setOpen(false);

  useEffect(() => {
    function handleClick(e) {
      if (!open) return;
      if (navRef.current && !navRef.current.contains(e.target)) close();
    }
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    function onResize() {
      if (window.innerWidth >= 768) close();
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <NavbarContainer ref={navRef} role="navigation" aria-label="Main">
      <NavContent>
        <Logo to="/">
          <LogoImage src={logoImg} alt="AI Building Skills Logo" />
        </Logo>

        <Spacer />

        <NavLinks>
          <NavLink to="/">Home</NavLink>
          {/* <NavLink to="/ebook">Download eBook</NavLink> */}
          <NavLink to="/signup-webinar">Upcoming Webinars</NavLink>
          <NavLink to="/prompt-gallery">Prompt Gallery</NavLink>
          <NavLink to="/blog">Blogs</NavLink>
          <NavLink to="/about-me">About me</NavLink>
          {/* <NavLink to="/ai-agents">AI Agents</NavLink> */}
          <DashboardButton href={CONSTRUCTION_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
            Construction Dashboard ↗
          </DashboardButton>
          <CTAButton to="/ebook">Get Free eBook</CTAButton>
        </NavLinks>

        <MobileMenuButton
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={toggle}
        >
          {open ? '✕' : '☰'}
        </MobileMenuButton>
      </NavContent>

      <MobileMenu id="mobile-nav" $open={open}>
        <MobileNavLinks>
          <MobileNavLink to="/" onClick={close}>
            Home
          </MobileNavLink>
          {/* <MobileNavLink to="/ebook" onClick={close}>
            Download Free eBook
          </MobileNavLink> */}
          <MobileNavLink to="/signup-webinar" onClick={close}>
            Upcoming Webinars
          </MobileNavLink>
          <MobileNavLink to="/prompt-gallery" onClick={close}>
           Prompt Gallery
          </MobileNavLink>
          <MobileNavLink to="/blog" onClick={close}>
            Blogs
          </MobileNavLink>
          <MobileNavLink to="/about-me" onClick={close}>
            About me
          </MobileNavLink>
          {/* <MobileNavLink to="/ai-agents" onClick={close}>
           AI Agents
          </MobileNavLink> */}
          <MobileDashboardButton
            href={CONSTRUCTION_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            Construction Dashboard ↗
          </MobileDashboardButton>
          <CTAButton to="/ebook" onClick={close} style={{ textAlign: 'center' }}>
            Get Free eBook
          </CTAButton>
        </MobileNavLinks>
      </MobileMenu>
    </NavbarContainer>
  );
};

export default Navbar;
